import {
  IGamePlexCategory,
  IGamesProviderNormalised,
  IGetFeedCategoryResponse,
  IGetFeedProviderResponse,
  INormalizedGame
} from '../../types';
import {
  IGetFeedProviderResponseWithFormattedGames,
  ISingleProviderResponseBase
} from '../../types/api/plex';
import { getGameGroup } from '../../utils';
import { IFeedCategoriesNormalized, IGamesStore } from '../types';

export const normalizePlexSingleProviderResponse = (
  data: IGetFeedProviderResponse
): IGetFeedProviderResponseWithFormattedGames => ({
  ...data,
  games: data.games.map((game) => ({
    ...game,
    providerId: data.id,
    providerName: data.name
  }))
});

export const normalizePlexMultiProviderResponse = (
  data: IGetFeedProviderResponse[] = []
) =>
  data.map((providerData) => normalizePlexSingleProviderResponse(providerData));

export const normalizePlexGames = (
  gamesArr: IGamePlexCategory[]
): IGamesStore => {
  const normalized = gamesArr.reduce<IGamesStore>(
    (acc, game) => {
      return {
        data: Object.assign(acc.data, {
          [game.id]: normalizeGameFromPlex(game)
        }),
        ids: [...acc.ids, game.id],
        launchIdToGameId: Object.assign(acc.launchIdToGameId, {
          [game.launchId]: game.id
        })
      };
    },
    { data: {}, ids: [], launchIdToGameId: {} }
  );
  return normalized;
};

export const normalizeGameFromPlex = (
  game: IGamePlexCategory
): INormalizedGame => {
  return {
    ...game,
    hasDemoSupport: game.supportDemo,
    provider: game.providerId,
    gameCode: game.id,
    gameId: game.id,
    gameName: game.name,
    group: getGameGroup(game.gameTypes),
    launchGameId: game.launchId
  };
};

export const normalizeProviderFromPlex = (
  provider: ISingleProviderResponseBase
): IGamesProviderNormalised => {
  return {
    ...provider,
    providerId: provider.id,
    providerName: provider.name,
    providerLogo: provider.logo,
    providerColoredLogo: provider.logoColored,
    serviceId: provider.serviceId?.toString()
  };
};

export const normalizePlexCategories = (
  categories: IGetFeedCategoryResponse[]
): {
  categories: IFeedCategoriesNormalized;
  games: Record<string, INormalizedGame>;
  launchIdToGameId: Record<number, string>;
} => {
  const games = {};
  const launchIdToGameId = {};
  const normalizedCategories: IFeedCategoriesNormalized = {
    data: {},
    ids: []
  };

  categories.forEach((category) => {
    const normalizedGames = normalizePlexGames(category.games);
    const normalizedInstantGames = normalizePlexGames(category.instantGames);
    Object.assign(games, {
      ...normalizedGames.data,
      ...normalizedInstantGames.data
    });
    Object.assign(launchIdToGameId, {
      ...normalizedGames.launchIdToGameId,
      ...normalizedInstantGames.launchIdToGameId
    });

    const { name, ...categoryRest } = category;

    normalizedCategories.data[category.id] = {
      ...categoryRest,
      title: name,
      games: normalizedGames.ids,
      instantGames: normalizedInstantGames.ids
    };
    normalizedCategories.ids.push(category.id);
  });

  return { categories: normalizedCategories, games, launchIdToGameId };
};

export const normalizePlexProviders = (
  plexProviders: IGetFeedProviderResponseWithFormattedGames[]
): {
  providers: Record<string, IGamesProviderNormalised>;
  providersIds: string[];
  serviceIdToProviderId: Record<string, string>;
  providersGames: Record<string, string[]>;
  providersTotalGames: Record<string, number>;
  games: Record<string, INormalizedGame>;
  launchIdToGameId: Record<number, string>;
} => {
  const providers: Record<string, IGamesProviderNormalised> = {};
  const providersIds: string[] = [];
  const serviceIdToProviderId: Record<string, string> = {};
  const providersGames: Record<string, string[]> = {};
  const providersTotalGames: Record<string, number> = {};

  const games = {};
  const launchIdToGameId = {};

  plexProviders.forEach((provider) => {
    const { games: providerGames, total, ...providerRest } = provider;
    const normalizedGames = normalizePlexGames(providerGames);

    // TODO: remove field duplicates from provider object between feed provider / plex provider
    providers[provider.id] = normalizeProviderFromPlex(providerRest);

    // TODO: merge provider related info int providers data object ? total games / first page games
    providersGames[provider.id] = normalizedGames.ids;
    providersTotalGames[provider.id] = total;
    providersIds.push(provider.id);
    serviceIdToProviderId[provider.serviceId] = provider.id;

    Object.assign(games, normalizedGames.data);
    Object.assign(launchIdToGameId, normalizedGames.launchIdToGameId);
  });

  return {
    providers,
    providersIds,
    serviceIdToProviderId,
    providersGames,
    providersTotalGames,
    games,
    launchIdToGameId
  };
};
